import { localNumber } from '../../../lib/units';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export interface FileLike {
  name: string;
  type: string;
  size: number;
}

export type FileKind = 'image' | 'pdf';
export type RejectReason = 'unsupported' | 'too-large';

export function isPdf(f: Pick<FileLike, 'name' | 'type'>): boolean {
  return f.type === 'application/pdf' || (f.type === '' && /\.pdf$/i.test(f.name));
}

/** Image or PDF the board can store; images over the upload limit are rejected (PDFs too). */
export function classifyFile(f: FileLike, maxBytes: number): FileKind | RejectReason {
  const kind = IMAGE_TYPES.includes(f.type) ? 'image' : isPdf(f) ? 'pdf' : null;
  if (kind === null) return 'unsupported';
  return f.size > maxBytes ? 'too-large' : kind;
}

export interface Partitioned<F extends FileLike> {
  images: F[];
  pdfs: F[];
  rejected: { file: F; reason: RejectReason }[];
}

export function partitionFiles<F extends FileLike>(
  files: readonly F[],
  maxBytes: number,
): Partitioned<F> {
  const out: Partitioned<F> = { images: [], pdfs: [], rejected: [] };
  for (const file of files) {
    const k = classifyFile(file, maxBytes);
    if (k === 'image') out.images.push(file);
    else if (k === 'pdf') out.pdfs.push(file);
    else out.rejected.push({ file, reason: k });
  }
  return out;
}

/** Portable board files (U7) and old JSON exports (v1). */
export function isBoardFileName(name: string): boolean {
  return /\.(stageplottter|json)$/i.test(name);
}

/** A picked file the board import / replace takes: a board file name or JSON content. */
export function acceptsBoardFile(file: { name: string; type: string }): boolean {
  return isBoardFileName(file.name) || file.type === 'application/json';
}

/** Readable size, e.g. "10 MB". */
export function formatMb(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  // decimal comma in German (OIL-201)
  return `${mb >= 10 || Number.isInteger(mb) ? Math.round(mb) : localNumber(mb, 1)} MB`;
}
